import { PreviewRequest, PreviewResponse } from '@shared/interfaces';
import {
  LoanEnginePortfolioConfiguration,
  LoanEngineProduct,
} from '@shared/interfaces/LoanEnginePortfolioConfiguration';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ArrangementRequest } from '@shared/interfaces/Arrangement';
import { UrlBuilder } from 'http-url-builder';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CompatibleBaseRate } from '@brand-settings-module/pages/portfolio-config/compatible-base-rate/interfaces/compatible-base-rate.interface';

@Injectable({
  providedIn: 'root',
})
export class LoanEngineApiService {
  public baseUrl: UrlBuilder = new UrlBuilder(environment.apim.baseUrl).addPath(
    environment.apim.apiSlugs.loanEngineApi
  );

  public baseUrlLoanEngine: UrlBuilder = new UrlBuilder(
    environment.apim.baseUrl
  ).addPath(environment.apim.apiSlugs.loanEngine);

  constructor(private http: HttpClient) {}

  public getAccountSummary(accountId: number): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .build();
    return this.http.get<any>(fullUrl).toPromise();
  }

  public getAccountSummaryObservable(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .build();
    return this.http.get<any>(fullUrl);
  }

  public getQuotations(accountId: number): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('account')
      .addPath(accountId?.toString())
      .build();
    return this.http.get<any>(fullUrl).toPromise();
  }

  public getQuotationsTypes(accountId: number): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('types')
      .build();
    return this.http.get<any>(fullUrl).toPromise();
  }

  public getQuotationById(quoteId: number): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath(quoteId?.toString())
      .build();
    return this.http.get<any>(fullUrl).toPromise();
  }

  public getQuotationCharges(accountId: number): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('charges')
      .build();
    return this.http.get<any>(fullUrl).toPromise();
  }

  public generateFullQuotationPreview(item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('full')
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public saveFullQuotation(item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('full')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public executeFullQuotation(item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('full')
      .addPath('execute')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public generatePartialQuotationPreview(item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('partial')
      .addPath('preview ')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public savePartialQuotation(item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('partial')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public executePartialQuotation(item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath('partial')
      .addPath('execute')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public reverseTransaction(item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('transaction')
      .addPath('reverse ')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public getTransactionCodes(): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('transaction')
      .addPath('codes')
      .build();
    return this.http.get<string[]>(fullUrl).toPromise();
  }

  public getTransactionsById(id: number) {
    const fullUrl: string = this.baseUrl
      .addPath('transaction')
      .addPath('account')
      .addPath(id?.toString())
      .addPath('codes')
      .build();
    return this.http.get<any>(fullUrl);
  }

  public getTransactionPreview(accountId: number, item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('transaction')
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public saveTransaction(accountId: number, item: any): Promise<object> {
    const fullUrl: string = this.baseUrl
      .addPath('transaction')
      .addPath('account')
      .addPath(accountId?.toString())
      .build();
    return this.http.post(fullUrl, item).toPromise();
  }

  public getTransactions(
    accountId: number,
    filterValues: any[] | undefined
  ): Observable<any> {
    let fullUrl = this.baseUrl
      .addPath('transaction')
      .addPath('account')
      .addPath(accountId?.toString())
      .build();

    if (filterValues) {
      let params = new HttpParams();
      filterValues.forEach((el) => {
        params = params.append(el.key, el.value);
      });
      return this.http.get<any>(fullUrl, { params: params });
    }
    return this.http.get<any>(fullUrl);
  }

  public getTranslations(language: string): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('config')
      .addPath('translations')
      .addPath(language)
      .build();
    return this.http.get<string[]>(fullUrl).toPromise();
  }

  public translations() {
    let enTranslations = localStorage.getItem('loan-engine-en-translations')
      ? JSON.parse(localStorage.getItem('loan-engine-en-translations'))
      : undefined;
    let esTranslations = localStorage.getItem('loan-engine-es-translations')
      ? JSON.parse(localStorage.getItem('loan-engine-es-translations'))
      : undefined;
    let translations = { en: enTranslations, es: esTranslations };
    return translations;
  }

  public getSystemDate(): Promise<any> {
    const fullUrl: string = this.baseUrl
      .addPath('config')
      .addPath('systemDate')
      .build();
    return this.http.get<string[]>(fullUrl).toPromise();
  }

  public getAccountHistory(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('edit')
      .addPath('history')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getAccountChangeInputs(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('edit')
      .addPath('inputs')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public evaluateAccountChanged(
    accountId: number,
    data: PreviewRequest
  ): Observable<PreviewResponse> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('edit')
      .addPath('preview')
      .build();
    return this.http.post<PreviewResponse>(fullUrl, data);
  }

  public saveAccountChanged(
    accountId: number,
    data: PreviewRequest
  ): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('edit')
      .build();
    return this.http.post(fullUrl, data);
  }

  public getArrangementsToPay(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('arrangement')
      .addPath('account')
      .addPath(accountId?.toString())
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getArrangementsData(arrangementId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('arrangement')
      .addPath(arrangementId?.toString())
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public arrangementsToPayPreview(data: ArrangementRequest): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('arrangement')
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, data);
  }

  public saveArrangementsToPay(data: ArrangementRequest): Observable<any> {
    const fullUrl: string = this.baseUrl.addPath('arrangement').build();
    return this.http.post(fullUrl, data);
  }

  public cancelArrangementsToPay(id: number, reason: string): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('arrangement')
      .addPath(id?.toString())
      .addPath('cancel')
      .addQueryParam('reason', reason)
      .build();
    return this.http.put(fullUrl, {});
  }

  public getBaseRates(): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('config')
      .addPath('baseRates')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public setBaseRate(item: any): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('config')
      .addPath('baseRate')
      .build();
    return this.http.post(fullUrl, item);
  }

  public getSwitchableProducts(accountId: number): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('switchableProducts')
      .build();

    return this.http.get<any>(fullUrl);
  }

  public switchProductPreview(id: number, data: any): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('account')
      .addPath(id?.toString())
      .addPath('switchProduct')
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, data);
  }

  public switchProduct(id: number, data: any): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('account')
      .addPath(id?.toString())
      .addPath('switchProduct')
      .build();
    return this.http.post(fullUrl, data);
  }

  public switchProductHistory(accountId: number): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('switchProduct')
      .addPath('history')
      .build();

    return this.http.get<any>(fullUrl);
  }

  public getTransactionCodesGrid(): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('transactionCodes')
      .build();
    return this.http.get<any>(fullUrl);
  }

  public getTariff(): Observable<any> {
    const fullUrl = this.baseUrl.addPath('config').addPath('tariffs').build();
    return this.http.get<any>(fullUrl);
  }

  public getTariffById(idTariffValue: number): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('tariff')
      .addPath(idTariffValue?.toString())
      .addPath('excludedFees')
      .build();
    return this.http.get<any>(fullUrl);
  }

  public getDebtTypesModal(): Observable<any> {
    const fullUrl = this.baseUrl.addPath('config').addPath('debtTypes').build();
    return this.http.get<any>(fullUrl);
  }

  public getWaterfallTypes(): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('waterfallTypes')
      .build();
    return this.http.get<any>(fullUrl);
  }

  public getTransactionCodeGroups(): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('transactionCodeGroups')
      .build();
    return this.http.get<any>(fullUrl);
  }

  public newTransactionCode(data: {
    codeDescription: string;
    codeGroup: string;
    waterfallType: string;
    debtTypeName: string;
  }): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('transactionCodes')
      .addQueryParam('transactionCode', data)
      .build();
    return this.http.post<any>(fullUrl, data);
  }

  public editTransactionCode(id: number): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('transactionCodes')
      .addPath('deactivate')
      .addPath(id?.toString())
      .build();
    return this.http.post<any>(fullUrl, id?.toString());
  }

  public dueDayChangePreview(
    id: number,
    newDueDay: number,
    stubInterestAction: string
  ): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(id.toString())
      .addPath('changeDueDate')
      .addPath('preview')
      .build();

    const body = {
      newDueDay: newDueDay,
      stubInterestAction: stubInterestAction,
    };
    return this.http.post<any>(fullUrl, body);
  }

  public dueDayChange(
    id: number,
    newDueDay: number,
    stubInterestAction: string
  ): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(id.toString())
      .addPath('changeDueDate')
      .build();

    const body = {
      newDueDay: newDueDay,
      stubInterestAction: stubInterestAction,
    };
    return this.http.post<any>(fullUrl, body);
  }

  public updateFee(data: {
    tariffId: number;
    feeId: number;
    amount: number;
    active: boolean;
  }): Observable<any> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('tariff')
      .addQueryParam('updateFee', data)
      .build();
    return this.http.post<any>(fullUrl, data);
  }

  public getDiaries(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('diaries')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getStandingData(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('standingData')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getRateHistory(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('rateHistory')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getInterestChargeHistory(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('interestChargeHistory')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public verticalLogData(item: any): Observable<any> {
    const fullUrl: string = this.baseUrlLoanEngine
      .addPath('internal')
      .addPath('VerticalLog')
      .addPath('Log')
      .build();
    return this.http.post<any>(fullUrl, item);
  }

  public previewBorrowback(accountId: number, data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('borrowback')
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, data);
  }

  public approveBorrowback(accountId: number, data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('borrowback')
      .build();
    return this.http.post(fullUrl, data);
  }

  public previewUnderpayment(accountId: number, data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('underpayment')
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, data);
  }

  public approveUnderpayment(accountId: number, data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('underpayment')
      .build();
    return this.http.post(fullUrl, data);
  }

  public cancelUnderpayment(
    accountId: number,
    reop: boolean
  ): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('underpayment')
      .addPath('cancel')
      .addQueryParam('reop', reop)
      .build();
    return this.http.post(fullUrl, {});
  }

  public previewPaymentHoliday(
    accountId: number,
    data: any
  ): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('paymentHoliday')
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, data);
  }

  public approvePaymentHoliday(
    accountId: number,
    data: any
  ): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('paymentHoliday')
      .build();
    return this.http.post(fullUrl, data);
  }

  public cancelPaymentHoliday(
    accountId: number,
    reop: boolean
  ): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(accountId?.toString())
      .addPath('paymentHoliday')
      .addPath('cancel')
      .addQueryParam('reop', reop)
      .build();
    return this.http.post(fullUrl, {});
  }

  public enableAutoCreditSweep(id: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('transaction')
      .addPath('account')
      .addPath(id?.toString())
      .addPath('autoCreditSweep')
      .addPath('true')
      .build();
    return this.http.post(fullUrl, {});
  }

  public disableAutoCreditSweep(id: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('transaction')
      .addPath('account')
      .addPath(id?.toString())
      .addPath('autoCreditSweep')
      .addPath('false')
      .build();
    return this.http.post(fullUrl, {});
  }

  public getUndrawnBalance(id: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(id?.toString())
      .addPath('undrawnBalance')
      .build();
    return this.http.get<any>(fullUrl);
  }

  public saveUndrawnBalance(id: number, data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(id?.toString())
      .addPath('undrawnBalance')
      .build();
    return this.http.post(fullUrl, data);
  }

  public rollLoanEngine(data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath('roll')
      .addPath('batch')
      .build();
    return this.http.post(fullUrl, data);
  }

  public getTranches(id: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(id?.toString())
      .addPath('tranches')
      .build();
    return this.http.get<any>(fullUrl);
  }

  public previewDrawdown(id: number, data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(id?.toString())
      .addPath('drawdown')
      .addPath('preview')
      .build();
    return this.http.post(fullUrl, data);
  }

  public postDrawdown(id: number, data: any): Observable<object> {
    const fullUrl: string = this.baseUrl
      .addPath('account')
      .addPath(id?.toString())
      .addPath('drawdown')
      .build();
    return this.http.post(fullUrl, data);
  }

  public getOriginalProjection(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('projection')
      .addPath(accountId?.toString())
      .addPath('original')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getScheduledProjection(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('projection')
      .addPath(accountId?.toString())
      .addPath('scheduled')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getPaymentProjection(accountId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('projection')
      .addPath(accountId?.toString())
      .addPath('payment')
      .build();
    return this.http.get<string[]>(fullUrl);
  }

  public getPortfolioConfigurations(): Observable<
    LoanEnginePortfolioConfiguration[]
  > {
    const fullUrl = this.baseUrl.addPath('config').addPath('portfolio').build();

    return this.http.get<LoanEnginePortfolioConfiguration[]>(fullUrl);
  }

  public getPortfolioConfiguration(
    portfolioId: number
  ): Observable<LoanEnginePortfolioConfiguration> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('portfolio')
      .addPath(portfolioId.toString())
      .build();

    return this.http.get<LoanEnginePortfolioConfiguration>(fullUrl);
  }

  public createPortfolioConfiguration(
    data: LoanEnginePortfolioConfiguration
  ): Observable<LoanEnginePortfolioConfiguration> {
    const fullUrl = this.baseUrl.addPath('config').addPath('portfolio').build();

    return this.http.post<LoanEnginePortfolioConfiguration>(fullUrl, data);
  }

  public updatePortfolioConfiguration(
    portfolioId: number,
    data: LoanEnginePortfolioConfiguration
  ): Observable<LoanEnginePortfolioConfiguration> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('portfolio')
      .addPath(portfolioId.toString())
      .build();

    return this.http.put<LoanEnginePortfolioConfiguration>(fullUrl, data);
  }

  public getPortfolioProducts(): Observable<LoanEngineProduct[]> {
    const fullUrl = this.baseUrl.addPath('config').addPath('products').build();

    return this.http.get<LoanEngineProduct[]>(fullUrl);
  }

  public redemptionReversal(quotationId: number): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('quotation')
      .addPath(quotationId?.toString())
      .addPath('reverse')
      .build();

    return this.http.post(fullUrl, {});
  }

  public getCompatibleBaseRates(): Observable<CompatibleBaseRate[]> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('compatibleBaseRates')
      .build();

    return this.http.get<CompatibleBaseRate[]>(fullUrl);
  }

  public getCompatibleBaseRateById(
    compatibleBaseRateId: number
  ): Observable<CompatibleBaseRate> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('compatibleBaseRates')
      .addPath(compatibleBaseRateId.toString())
      .build();

    return this.http.get<CompatibleBaseRate>(fullUrl);
  }

  public createCompatibleBaseRate(
    data: CompatibleBaseRate
  ): Observable<CompatibleBaseRate> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('compatibleBaseRates')
      .build();

    return this.http.post<CompatibleBaseRate>(fullUrl, data);
  }

  public deleteCompatibleBaseRate(
    compatibleBaseRateId: number
  ): Observable<CompatibleBaseRate> {
    const fullUrl = this.baseUrl
      .addPath('config')
      .addPath('compatibleBaseRates')
      .addPath(compatibleBaseRateId.toString())
      .build();

    return this.http.delete<CompatibleBaseRate>(fullUrl);
  }
}
